<template>
  <v-card class="mb-8 pa-4 rounded-lg elevation-2">
    <!-- Header Row -->
    <div class="d-flex align-center justify-space-between mb-3">
      <h3 class="text-subtitle-1 font-weight-medium text-grey-darken-4">
        {{ question }}
      </h3>

      <div class="d-flex align-center">
        <v-chip color="blue-lighten-4" text-color="blue-darken-2" small>
          Total Respondents: {{ totalResponses }}
        </v-chip>

        <v-btn-toggle
          v-model="chartType"
          class="ml-3"
          density="compact"
          rounded
        >
          <v-btn value="pie" size="small">Pie</v-btn>
          <v-btn value="column" size="small">Bar</v-btn>
        </v-btn-toggle>
      </div>
    </div>
    <v-checkbox
      v-if="chartType === 'column'"
      v-model="sortDescending"
      label="Sort Descending"
      hide-details
      density="compact"
      class="ml-3 mb-5"
    />

    <!-- Chart -->
    <Chart :options="chartOptions" />
  </v-card>
</template>

<script>
import { Chart } from "highcharts-vue";
import Highcharts from "highcharts";
import Exporting from "highcharts/modules/exporting";
import ExportData from "highcharts/modules/export-data";

Exporting(Highcharts);
ExportData(Highcharts);

export default {
  name: "SurveyChart",
  components: { Chart },
  props: {
    question: { type: String, required: true },
    answers: { type: Array, required: true }, // [{ option, count }]
    totalResponses: { type: Number, required: true },
  },
  data() {
    return {
      chartType: "pie",
      sortDescending: false, // ✅ state for checkbox
    };
  },
  computed: {
    chartOptions() {
      // Calculate the total number of selections (sum of all counts)
      const totalSelections = this.answers.reduce((sum, a) => sum + a.count, 0);

      // Prepare data (capitalized + optional sorting)
      // Text-box answers can include null options from empty submissions
      let processedAnswers = this.answers.map((a) => {
        const optionLabel =
          a.option == null || a.option === ""
            ? "(Empty)"
            : String(a.option);
        return {
          name:
            optionLabel.charAt(0).toUpperCase() + optionLabel.slice(1),
          y: a.count,
          percentage:
            totalSelections > 0 ? (a.count / totalSelections) * 100 : 0,
        };
      });

      // Sort if needed (only for bar chart)
      if (this.chartType === "column" && this.sortDescending) {
        processedAnswers = processedAnswers.sort((a, b) => b.y - a.y);
      }

      return {
        chart: {
          type: this.chartType,
          backgroundColor: "transparent",
        },
        title: { text: null },
        tooltip: {
          pointFormat: "<b>{point.percentage:.1f}%</b> ({point.y} selections)",
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: "pointer",
            dataLabels: {
              enabled: true,
              format: "<b>{point.name}</b>: {point.percentage:.1f} %",
              style: { fontSize: "12px" },
            },
            showInLegend: true,
          },
          column: {
            borderRadius: 4,
            colorByPoint: true,
            pointPadding: 0.2,
            groupPadding: 0.1,
            maxPointWidth: 60,
            dataLabels: {
              enabled: true,
              format: "{point.percentage:.1f}%",
              style: { fontSize: "12px" },
            },
          },
        },
        legend: {
          enabled: this.chartType === "pie",
          layout: "vertical",
          align: "right",
          verticalAlign: "middle",
          itemStyle: { fontSize: "13px" },
        },
        xAxis:
          this.chartType === "column"
            ? {
                categories: processedAnswers.map((a) => a.name),
                labels: { style: { fontSize: "12px" } },
              }
            : undefined,
        yAxis:
          this.chartType === "column"
            ? {
                min: 0,
                title: { text: "Selections" },
                allowDecimals: false,
              }
            : undefined,
        series: [
          {
            name: "Selections",
            colorByPoint: true,
            data: processedAnswers,
          },
        ],
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: [
                "downloadPNG",
                "downloadJPEG",
                "downloadPDF",
                "downloadSVG",
                "downloadCSV",
                "downloadXLS",
              ],
            },
          },
        },
      };
    },
  },
};
</script>
